import { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Collapse, alpha, makeStyles } from '@material-ui/core';
import DotsMenu from '../../dotsMenu';
import { prepareDotsMenuItems } from '../../../../utility/leveling';
import CustomButton from '../../customButton';
import TextBoxWithTooltip from '../../textBoxWithTooltip';
import ActionButton from '../../actionButton';
import { isArrayEmpty } from '../../../../utility/helpers';
import { ACTION_BUTTON_TYPES } from '../../actionButton/config';
import LevelingTracks from './levelingTracks';

const useStyles = makeStyles(
  ({ palette: { primary }, spacing, breakpoints }) => ({
    header: {
      backgroundColor: primary.bluish8,
      borderRadius: 8,
      boxSizing: 'border-box',
      border: `1px solid ${primary.bluish6}`,
      marginBottom: spacing(4),
      cursor: 'pointer',
      alignItems: 'center',
      height: 40,
      display: 'grid',
      gridTemplateColumns:
        'minmax(36px, 36px) minmax(140px, 1487px) minmax(60px, 60px)',
      [breakpoints.up('xLg')]: {
        gridTemplateColumns:
          'minmax(36px, 36px) minmax(140px, 2150px) minmax(60px, 60px)',
      },
      '& $dragButton': {
        opacity: 0,
        transition: 'opacity .3s ease',
      },
      '&:hover': {
        boxShadow: `0px 2px 8px 0px ${alpha(primary.black, 0.17)}`,
        transition: 'box-shadow .3s ease',
        '& $dragButton': {
          opacity: ({ isVisible }) => {
            return isVisible ? 1 : 0;
          },
          transition: 'opacity .3s ease',
          cursor: ({ isVisible }) => {
            return isVisible ? 'pointer' : 'grabbing';
          },
        },
      },
      '&:active': {
        '& > div:first-of-type': {
          opacity: 1,
          transition: 'opacity .3s ease',
        },
        boxShadow: `0px 2px 8px 0px ${alpha(primary.black, 0.17)}`,
        transition: 'box-shadow .3s ease',
      },
    },
    headerReadOnly: {
      width: '100%',
      gridTemplateColumns: 'minmax(90px, 1100px) minmax(60px, 60px)',
      [breakpoints.up('xLg')]: {
        gridTemplateColumns: 'minmax(90px, 2100px) minmax(60px, 60px)',
      },
    },
    headerDisabled: {
      gridTemplateColumns: 'minmax(90px, 1441px) minmax(30px, 40px)',
      [breakpoints.up('xLg')]: {
        gridTemplateColumns: 'minmax(90px, 2156px) minmax(30px, 40px)',
      },
    },
    headerWithDelete: {
      gridTemplateColumns: 'minmax(140px, 1487px) minmax(60px, 60px)',
      [breakpoints.up('xLg')]: {
        gridTemplateColumns: 'minmax(140px, 2150px) minmax(60px, 60px)',
      },
    },
    disableCollapse: {
      cursor: 'default',
    },
    actionsWrapper: {
      display: 'flex',
      alignItems: 'center',
      gap: spacing(1),
      marginRight: spacing(2),
      justifyContent: 'flex-end',
    },
    templateActionButton: {
      marginRight: 0,
    },
    actionButton: {
      width: 24,
      height: 24,
    },
    expandIcon: {
      transform: 'rotate(0deg)',
      transition: 'transform .3s cubic-bezier(0.77, 0.2, 0.05, 1)',
    },
    expandIconOpened: {
      transform: 'rotate(180deg)',
    },
    titleText: {
      boxSizing: 'border-box',
      fontSize: 14,
      lineHeight: '18px',
      paddingLeft: spacing(8),
    },
    titleWithDragButton: {
      paddingLeft: 0,
    },
    button: {
      borderRadius: 8,
      marginBottom: spacing(4),
      marginTop: spacing(1),
      width: '100%',
      justifyContent: 'center',
    },
    dragButton: {
      position: 'relative',
      width: 22,
      height: 22,
      marginLeft: spacing(2),
      flexShrink: 0,
      marginRight: spacing(1.5),
      zIndex: 11,
      '& button': {
        width: 22,
        height: 22,
      },
      '$header:hover': {
        opacity: ({ isVisible }) => {
          return isVisible ? 1 : 0;
        },
        transition: 'opacity .3s ease',
        cursor: ({ isVisible }) => {
          return isVisible ? 'pointer' : 'grabbing';
        },
      },
    },
    dragIcon: {
      width: 8,
      height: 14,
    },
    dragTooltipLabel: {
      fontFamily: 'ProximaNova-Regular',
    },
    tracksWrapper: {
      marginLeft: 40,
    },
    disabledActions: {
      cursor: 'default',
      justifyContent: 'end',
    },
  })
);

const LevelingFramework = ({
  translations,
  levelingFramework,
  dragRef = () => {},
  dragProps = null,
  isVisible = false,
  isReadOnly,
  canEdit = false,
  canDelete = false,
  onDuplicateFramework = () => {},
  onDuplicateTrack = () => {},
  onEditFrameworkTitle = () => {},
  onEditTrackTitle = () => {},
  handleDeleteFrameworkDialog = () => {},
  handleDeleteTrackDialog = () => {},
  handleCreateLevel = () => {},
  handleCreateTrack = () => {},
  onReorderTrack = () => {},
  handleMoveTrack = () => {},
  handleEditSalaries = () => {},
  onReorderLevel = () => {},
  isExpandedOnFirstOpen = false,
  toggleExpansion = () => {},
  isExpandedOnFirstOpenFramework = false,
  toggleExpansionFramework = () => {},
}) => {
  const classes = useStyles({ isVisible });
  const { name, tracks } = levelingFramework;
  const hasTracks = !isArrayEmpty(tracks);
  const [isExpanded, setIsExpanded] = useState(
    isExpandedOnFirstOpenFramework || false
  );
  const isExpandedDropdown = isExpanded || isExpandedOnFirstOpenFramework;
  const toggleExpand = () => {
    if (hasTracks || canEdit) {
      if (isExpandedOnFirstOpenFramework) {
        toggleExpansionFramework(false);
        setIsExpanded(false);
      }
      if (!isExpandedOnFirstOpenFramework) {
        setIsExpanded(prevExpanded => !prevExpanded);
      }
    }
  };

  return (
    <div ref={dragRef} {...(dragProps ? { ...dragProps.draggableProps } : {})}>
      <div
        className={classNames(classes.header, {
          [classes.headerReadOnly]: isReadOnly,
          [classes.headerDisabled]: !canEdit,
          [classes.headerWithDelete]: !canEdit && canDelete,
          [classes.disableCollapse]: !hasTracks,
        })}
        onClick={toggleExpand}
      >
        {canEdit && (
          <ActionButton
            className={classes.dragButton}
            iconClass={classes.dragIcon}
            tooltipLabelClass={classes.dragTooltipLabel}
            type={ACTION_BUTTON_TYPES.DRAG}
            tooltipText={translations.frameworkDragTooltip}
            {...(dragProps ? { ...dragProps.dragHandleProps } : {})}
          />
        )}
        <TextBoxWithTooltip
          className={classNames(classes.titleText, {
            [classes.titleWithDragButton]: canEdit,
          })}
          variant="h5"
          text={name}
        />
        <div
          className={classNames(classes.actionsWrapper, {
            [classes.disabledActions]: isReadOnly || !hasTracks,
          })}
        >
          {(canEdit || canDelete) && (
            <DotsMenu
              menuItems={prepareDotsMenuItems(
                translations,
                levelingFramework,
                levelingFramework,
                canEdit,
                canDelete,
                handleDeleteFrameworkDialog,
                onEditFrameworkTitle,
                onDuplicateFramework
              )}
              isDisabled={isReadOnly}
            />
          )}
          {(hasTracks || canEdit) && (
            <ActionButton
              className={classNames(
                { [classes.templateActionButton]: isReadOnly },
                classes.actionButton
              )}
              type={ACTION_BUTTON_TYPES.ARROW}
              iconClass={classNames(classes.expandIcon, {
                [classes.expandIconOpened]: isExpandedDropdown,
              })}
            />
          )}
        </div>
      </div>
      <Collapse in={isExpandedDropdown} timeout="auto" unmountOnExit>
        <div className={classes.tracksWrapper}>
          {hasTracks && (
            <LevelingTracks
              translations={translations}
              levelingTracks={levelingFramework?.tracks}
              levelingFramework={levelingFramework}
              canEdit={canEdit}
              canDelete={canDelete}
              isReadOnly={isReadOnly}
              isExpandedOnFirstOpen={isExpandedOnFirstOpen}
              onEditTrackTitle={onEditTrackTitle}
              onDuplicateTrack={onDuplicateTrack}
              onReorderTrack={onReorderTrack}
              handleDeleteTrackDialog={handleDeleteTrackDialog}
              onReorderLevel={onReorderLevel}
              handleCreateLevel={handleCreateLevel}
              handleMoveTrack={handleMoveTrack}
              handleEditSalaries={handleEditSalaries}
              toggleExpansion={toggleExpansion}
            />
          )}
          {!isReadOnly && canEdit && (
            <CustomButton
              className={classes.button}
              type="addWithTextDarkNewMedium"
              onClick={() => handleCreateTrack(levelingFramework)}
            >
              {translations.createTrackButton}
            </CustomButton>
          )}
        </div>
      </Collapse>
    </div>
  );
};

LevelingFramework.propTypes = {
  translations: PropTypes.object.isRequired,
  levelingFramework: PropTypes.object.isRequired,
  isReadOnly: PropTypes.bool.isRequired,
  handleEditSalaries: PropTypes.func,
  isVisible: PropTypes.bool,
  canEdit: PropTypes.bool,
  canDelete: PropTypes.bool,
  onDuplicateFramework: PropTypes.func,
  onDuplicateTrack: PropTypes.func,
  onReorderLevel: PropTypes.func,
  onReorderTrack: PropTypes.func,
  handleCreateLevel: PropTypes.func,
  handleDeleteFrameworkDialog: PropTypes.func,
  handleDeleteTrackDialog: PropTypes.func,
  handleCreateTrack: PropTypes.func,
  handleMoveTrack: PropTypes.func,
  onEditFrameworkTitle: PropTypes.func,
  onEditTrackTitle: PropTypes.func,
  dragProps: PropTypes.object,
  dragRef: PropTypes.func,
  isExpandedOnFirstOpen: PropTypes.bool,
  toggleExpansion: PropTypes.func,
  isExpandedOnFirstOpenFramework: PropTypes.bool,
  toggleExpansionFramework: PropTypes.func,
};

export default LevelingFramework;
